import { useTranslation } from 'core/i18n/i18n';
import { showErrorNotification, showSuccessNotification } from 'core/utils/notification-service';
import { parseRestErrorMessage } from 'core/utils/parse-rest-error.util';
import { useCallback, useEffect, useRef, useState } from 'react';
import { checkActiveDirectoryConnection } from '../service/active-directory.service';
import {
    ConnectionFormFieldErrors,
    ConnectionFormFieldName,
} from '../components/settings-integrations-active-directory-details/components/active-directory-connection-details-form/active-directory-connection-details-form.business';
import {
    ActiveDirectoryDetailsFormState,
    TestConnectionState,
    TestConnectionStatus,
} from '../types/active-directory.type';

const initialTestConnectionState: TestConnectionState = { status: TestConnectionStatus.IDLE };

interface UseActiveDirectoryTestConnectionProps {
    activeDirectoryId?: string;
    formState: ActiveDirectoryDetailsFormState;
}

export const useActiveDirectoryTestConnection = ({
    activeDirectoryId,
    formState,
}: UseActiveDirectoryTestConnectionProps) => {
    const { t } = useTranslation();
    const [testConnectionState, setTestConnectionState] = useState<TestConnectionState>(initialTestConnectionState);
    const [testConnectionDataPoints, setTestConnectionDataPoints] = useState<string[]>([]);
    const [fieldErrors, setFieldErrors] = useState<ConnectionFormFieldErrors>({});
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(
        () => () => {
            abortControllerRef.current?.abort();
            abortControllerRef.current = null;
        },
        []
    );

    const handleTestConnection = useCallback(
        () =>
            new Promise<void>((resolve, reject) => {
                abortControllerRef.current?.abort();
                const controller = new AbortController();
                abortControllerRef.current = controller;

                const { name, url, group, username, userGroup, password } = formState;

                const defaultErrorMessage = t(
                    'settings.integrations.active-directory.notification.testConnection.error.default-reason'
                );

                const connectionData = {
                    connectionId: activeDirectoryId,
                    name,
                    url,
                    group,
                    username,
                    userGroup,
                    password,
                };

                setTestConnectionState({ status: TestConnectionStatus.TESTING });
                checkActiveDirectoryConnection(connectionData, controller.signal)
                    .then(({ status, dataPoints }) => {
                        if (status === 'success') {
                            setTestConnectionState({ status: TestConnectionStatus.SUCCESS });
                            setTestConnectionDataPoints(dataPoints);
                            showSuccessNotification(
                                t('settings.integrations.active-directory.notification.testConnection.success')
                            );
                            resolve();
                        }

                        if (status === 'error') {
                            // TODO: errorReason from backend mocked due to missing implementation of clean errors. Also set field-level errors when backend returns them per field
                            // const message = errorReason || defaultErrorMessage;
                            // setFieldErrors({ url: message });
                            const message = defaultErrorMessage;
                            setTestConnectionDataPoints([]);
                            setTestConnectionState({
                                status: TestConnectionStatus.ERROR,
                                message,
                            });

                            showErrorNotification(
                                t('settings.integrations.active-directory.notification.testConnection.error', {
                                    errorMessage: message,
                                })
                            );
                            reject(new Error(message));
                        }
                    })
                    .catch((error) => {
                        if (error.name === 'AbortError') {
                            return;
                        }
                        setTestConnectionDataPoints([]);

                        const errorMessage =
                            error instanceof Error && error.name === 'PollingTimeoutError'
                                ? t('settings.integrations.active-directory.notification.testConnection.error.timeout')
                                : parseRestErrorMessage(
                                      error,
                                      t(
                                          'settings.integrations.active-directory.notification.testConnection.error.unknown-error'
                                      )
                                  );

                        setTestConnectionState({
                            status: TestConnectionStatus.ERROR,
                            message: errorMessage,
                        });
                        showErrorNotification(
                            t('settings.integrations.active-directory.notification.testConnection.error', {
                                errorMessage: errorMessage,
                            })
                        );
                        reject(new Error(errorMessage));
                    });
            }),
        [formState, activeDirectoryId, t]
    );

    const handleResetTestConnectionState = useCallback(() => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;

        setTestConnectionState(initialTestConnectionState);
        setTestConnectionDataPoints([]);
        setFieldErrors({});
    }, []);

    const clearFieldError = useCallback((field: string) => {
        const key = field as ConnectionFormFieldName;
        setFieldErrors((prev) => {
            if (!prev[key]) {
                return prev;
            }
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    return {
        testConnectionState,
        testConnectionDataPoints,
        fieldErrors,
        handleResetTestConnectionState,
        handleTestConnection,
        clearFieldError,
    };
};
